import { record, upper, type Row } from '../ability/context.js';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers.js';
import { normalizePhpListish } from '../../shared/php-listish.js';
import { encounterCreatureField, layerSlot } from '../../shared/array-types.js';
import { fullDefinition } from '../reactions/php-row-rules.js';
import { prepareInventoryDamageDefinitions } from './damage-definition-inputs.js';

type Readers = Parameters<typeof prepareInventoryDamageDefinitions>[1];
type Fetch = Parameters<typeof prepareInventoryDamageDefinitions>[2];
type Scope = { identity: (encounter: Row) => string; epoch: () => string };

/** Identities, containment and own composition overrides change destruction inputs.
 * Sparse PHP lists and wrapped fields use the existing readers. No catalog or
 * computed tree is copied. */
const memberId = (row: Row): string => upper(encounterCreatureField(record(row.encounter_creatures), 'encounter_creature_id'));
function signature(encounter: Row, members?: Map<string, string>): string {
  const parts: string[] = [], seen = new Set<Row>();
  members?.clear();
  const walk = (source: Row, parent: string): void => {
    if (seen.has(source)) return;
    seen.add(source);
    const id = upper(readInventoryField(source, 'inventory_id'));
    if (id) parts.push(JSON.stringify([parent, id, upper(readInventoryField(source, 'inventory_item_id')),
      upper(readInventoryField(source, 'possess_id')), layerSlot(source, 'item_composition')]));
    for (const key of ['encounter_inventories_arr', 'inventory', 'inventory_in_container'] as const)
      for (const child of readInventoryRows(source, key)) walk(child, id || parent);
    for (const child of normalizePhpListish(source.encounter_creatures_arr)) {
      const row = record(child);
      if (row) {
        const id = memberId(row), start = parts.length;
        walk(row, id);
        members?.set(id, parts.slice(start).sort().join('\n'));
      }
    }
  };
  walk(encounter, memberId(encounter));
  return parts.sort().join('\n');
}

/** The owning page creates ONE cache. Broadcasts mark its signature dirty; an unchanged
 * action takes only the roster guard and held-dependency Map probes. Mutation producers announce
 * encounter state / inventory adoption before another action can target the new rows. */
export function createInventoryDamageDefinitionWarmup(scope: Scope) {
  type State = { source: Row; dirty: boolean; roots: unknown[]; epoch: string;
    completed?: string; missing: Set<string>; held: Set<string>; members: Map<string, string>;
    pending?: Promise<void>; latest?: { read: Readers; fetch: Fetch } };
  const states = new Map<string | Row, State>();
  let readCache: (() => ReadonlyMap<string, unknown> | undefined) | undefined;
  const roots = (e: Row): unknown[] => [e.encounter_inventories_arr,
    Array.isArray(e.encounter_inventories_arr) ? e.encounter_inventories_arr.length : undefined,
    e.encounter_creatures_arr, Array.isArray(e.encounter_creatures_arr) ? e.encounter_creatures_arr.length : undefined];
  const stateFor = (encounter: Row): State => {
    const key = scope.identity(encounter) || encounter;
    let state = states.get(key);
    if (!state) {
      state = { source: encounter, dirty: true, roots: [], epoch: scope.epoch(), missing: new Set(), held: new Set(), members: new Map() };
    }
    states.delete(key); states.set(key, state);
    if (states.size > 8) states.delete(states.keys().next().value!);
    if (state.source !== encounter) { state.source = encounter; state.dirty = true; }
    return state;
  };
  const observe = (encounter: Row): void => { stateFor(encounter).dirty = true; };
  // Ordinary caster repaints never inspect the floor roster. Only a changed pack
  // invalidates the encounter's completed dependency discovery.
  const observeMember = (encounter: Row, member: Row): void => {
    const state = stateFor(encounter), id = memberId(member), current = signature(member);
    if (state.members.get(id) !== current) { state.members.set(id, current); state.dirty = true; }
  };
  // Continue an in-flight state's work directly: an evicted visit must not re-enter
  // the LRU when its fetch settles after the player has moved elsewhere.
  const prepareState = async (state: State, read: Readers, fetch: Fetch): Promise<void> => {
    state.latest = { read, fetch };
    if (state.pending) { await state.pending; return prepareState(state, state.latest.read, state.latest.fetch); }
    const encounter = state.source;
    // Commits advance encounter revisions without changing catalog availability.
    // Only catalog invalidation expires misses; inventory identities below detect arrivals.
    const epoch = scope.epoch();
    if (state.epoch !== epoch) {
      state.epoch = epoch;
      state.missing.clear(); state.held.clear(); delete state.completed; state.dirty = true;
    }
    const cache = readCache?.();
    if (state.completed !== undefined && [...state.held].some(id => !cache?.has(id))) delete state.completed;
    const currentRoots = roots(encounter);
    if (state.completed !== undefined && !state.dirty && currentRoots.every((value, i) => value === state.roots[i])) return;
    const current = signature(encounter, state.members);
    state.roots = currentRoots; state.dirty = false;
    if (state.completed === current) return;
    const held = new Set<string>();
    const tracked: Readers = { ...read, definition: id => {
      const definition = read.definition(id), key = upper(id);
      if (fullDefinition(definition, 'any') && readCache?.()?.get(key) === definition) held.add(key);
      return definition;
    } };
    const job = prepareInventoryDamageDefinitions([encounter], tracked, async ids => {
      const wanted = ids.filter(id => !state.missing.has(id));
      if (!wanted.length) return;
      await fetch(wanted);
      // A successfully answered batch omits missing / forbidden definitions from the
      // shared cache. Transport failures throw above and must never become negative hits.
      if (scope.epoch() === epoch)
        for (const id of wanted) if (!fullDefinition(tracked.definition(id), 'any')) state.missing.add(id);
    });
    state.pending = job;
    try {
      await job;
      if (scope.epoch() === epoch) { state.completed = current; state.held = held; }
    } finally {
      // Recheck arrivals during a real pass (including in-place nested edits), and
      // leave failures retryable even after an earlier completed signature.
      state.dirty = true;
      if (state.pending === job) delete state.pending;
    }
    return prepareState(state, state.latest.read, state.latest.fetch);
  };
  return { observe, observeMember,
    bindCache: (reader: NonNullable<typeof readCache>): void => { readCache = reader; },
    prepare: (encounter: Row, read: Readers, fetch: Fetch): Promise<void> => prepareState(stateFor(encounter), read, fetch) };
}
